import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, CheckCircleIcon, UploadCloudIcon } from 'lucide-react';
import { Job } from '../../lib/data';
import { Button } from '../ui/Button';
interface ApplyModalProps {
  job: Job;
  open: boolean;
  onClose: () => void;
}
export function ApplyModal({
  job,
  open,
  onClose
}: ApplyModalProps) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '',
    email: '',
    linkedin: '',
    note: '',
    file: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const update = (k: keyof typeof form, v: string) => setForm(f => ({
    ...f,
    [k]: v
  }));
  const reset = () => {
    setStep(1);
    setSubmitted(false);
    setForm({
      name: '',
      email: '',
      linkedin: '',
      note: '',
      file: ''
    });
  };
  const close = () => {
    onClose();
    setTimeout(reset, 300);
  };
  return <AnimatePresence data-id="element-150" data-mp="2imshw9fxfnxe">
      {open && <motion.div className="fixed inset-0 z-[60] flex items-end justify-center bg-ink/40 backdrop-blur-sm sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={close} role="dialog" aria-modal="true" aria-label={`Apply for ${job.title}`} data-id="element-151" data-mp="2ilr7862tp3g7">
          <motion.div className="relative flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-ivory-50 shadow-lift sm:rounded-3xl" initial={{
        y: 40,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 40,
        opacity: 0,
        scale: 0.98
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} onClick={e => e.stopPropagation()} data-id="element-152" data-mp="1sop2jyuumxf4">
            <button onClick={close} className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full text-ink-500 transition-colors hover:bg-ink/5 hover:text-ink" aria-label="Close" data-id="element-153" data-mp="2z5sztkqh8v5q">
              <XIcon className="h-5 w-5" data-id="element-154" data-mp="32p1kn0i672a0" />
            </button>

            <div className="overflow-y-auto p-8" data-id="element-155" data-mp="2xodtmetxylfs">
              {submitted ? <div className="flex flex-col items-center py-8 text-center" data-id="element-156" data-mp="2sp0y75p0861l">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 text-gold-dark" data-id="element-157" data-mp="2onywmi7m7hwg">
                    <CheckCircleIcon className="h-8 w-8" data-id="element-158" data-mp="3lere9ka75i0c" />
                  </span>
                  <h3 className="mt-6 font-serif text-2xl text-ink" data-id="element-159" data-mp="3gf5098mjnxqh">
                    Application received
                  </h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-ink-600" data-id="element-160" data-mp="3bnn3kwrk0jbb">
                    Thank you, {form.name.split(' ')[0] || 'there'}. A partner will review
                    your profile in confidence and be in touch within five working days.
                  </p>
                  <Button onClick={close} variant="primary" size="md" className="mt-8" data-id="element-161" data-mp="383abxqvv0w9">
                    Done
                  </Button>
                </div> : <>
                  <p className="text-xs uppercase tracking-widest2 text-gold-dark" data-id="element-162" data-mp="1qto69dxagobk">
                    {job.confidential ? 'Confidential Client' : job.company}
                  </p>
                  <h3 className="mt-2 font-serif text-2xl leading-snug text-ink" data-id="element-163" data-mp="1zuw5pzbjjv78">
                    {job.title}
                  </h3>

                  <div className="mt-6 flex items-center gap-2" data-id="element-164" data-mp="24tfqhweef0y0">
                    {[1, 2].map(s => <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${step >= s ? 'bg-gold' : 'bg-ink/10'}`} data-id="element-165" data-mp="29rr0c74p1bg8" />)}
                  </div>
                  <p className="mt-3 text-xs text-ink-500" data-id="element-166" data-mp="2nm9wi0u1niq3">
                    Step {step} of 2 — {step === 1 ? 'Your details' : 'Your candidacy'}
                  </p>

                  {step === 1 ? <div className="mt-6 space-y-4" data-id="element-167" data-mp="2ev9qatu7je1n">
                      <Field label="Full name" value={form.name} onChange={v => update('name', v)} placeholder="Alexandra Reid" data-id="element-168" data-mp="29yjbsay1wqic" />
                      <Field label="Email" type="email" value={form.email} onChange={v => update('email', v)} placeholder="alexandra@email.com" data-id="element-169" data-mp="2jokx4uz0rutk" />
                      <Field label="LinkedIn profile" value={form.linkedin} onChange={v => update('linkedin', v)} placeholder="linkedin.com/in/…" data-id="element-170" data-mp="25xzbiyi6l6rz" />
                      <Button onClick={() => setStep(2)} variant="primary" size="lg" className="mt-2 w-full" data-id="element-171" data-mp="3cd9qu8xqwxva">
                        Continue
                      </Button>
                    </div> : <div className="mt-6 space-y-4" data-id="element-172" data-mp="3axe412i5xfbc">
                      <div data-id="element-173" data-mp="3ew4tqokatuof">
                        <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-ink-600" data-id="element-174" data-mp="3exertdr5j2v2">
                          Résumé / CV
                        </span>
                        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-ink/25 bg-ivory-100 px-4 py-8 text-center transition-colors hover:border-gold hover:bg-gold/5" data-id="element-175" data-mp="bf4t28sq8map">
                          <UploadCloudIcon className="h-6 w-6 text-gold-dark" data-id="element-176" data-mp="1o0bd1bo2nc6" />
                          <span className="text-sm text-ink-600" data-id="element-177" data-mp="penxaqif3dhq">
                            {form.file || 'Click to upload (PDF, DOCX)'}
                          </span>
                          <input type="file" className="hidden" onChange={e => update('file', e.target.files?.[0]?.name || '')} data-id="element-178" data-mp="141mockdd45vx" />
                        </label>
                      </div>
                      <div data-id="element-179" data-mp="l3s6rhy64m1x">
                        <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-ink-600" data-id="element-180" data-mp="l41v713kel5i">
                          A note to the search team
                        </span>
                        <textarea value={form.note} onChange={e => update('note', e.target.value)} rows={4} placeholder="Briefly, why this mandate resonates with you…" className="w-full resize-none rounded-xl border border-ink/15 bg-ivory-100 px-4 py-3 text-sm text-ink placeholder:text-ink-400 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold" data-id="element-181" data-mp="caow1hewqnaz" />
                      </div>
                      <div className="flex gap-3 pt-2" data-id="element-182" data-mp="3ocx1o4350emj">
                        <Button onClick={() => setStep(1)} variant="outline" size="lg" className="flex-1" data-id="element-183" data-mp="3tdl03atp3xqs">
                          Back
                        </Button>
                        <Button onClick={() => setSubmitted(true)} variant="gold" size="lg" className="flex-1" data-id="element-184" data-mp="35ngtcul7nhvk">
                          Submit
                        </Button>
                      </div>
                    </div>}
                </>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text'
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return <label className="block" data-id="element-185" data-mp="ey24mfn5b800">
      <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-ink-600" data-id="element-186" data-mp="eybw67nu9n7t">
        {label}
      </span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full rounded-xl border border-ink/15 bg-ivory-100 px-4 py-3 text-sm text-ink placeholder:text-ink-400 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold" data-id="element-187" data-mp="nxp8r979erwr" />
    </label>;
}